/**
 * Mobile Plan Service
 *
 * SECURITY UPDATE: April 2025
 * All direct references to backend URLs have been replaced with secure server proxies
 * to prevent exposing sensitive backend URLs in client-side code.
 */
export const useMobilePlanService = () => {
  /**
   * Get network balance for a customer
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const networkBalance = async (customerId: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/network-balance`, {
        method: "POST",
        credentials: "include",
        body: { customerId },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  /**
   * Get current services for the authenticated user
   * Uses secure server proxy endpoint instead of direct backend URL
   */
  const currentServices = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/current-services`, {
        method: "GET",
        credentials: "include",
      });
      return response?.result || null;
    } catch (error) {
      // PostHog: Track API errors for monitoring
      if (typeof window !== 'undefined') {
        try {
          const { trackApiError } = usePostHog();
          const statusCode = (error as any)?.status || (error as any)?.statusCode || 0;
          trackApiError('/api/integration/current-services', statusCode, (error as any)?.message || 'Unknown error');
        } catch (posthogError) {
          console.warn('[PostHog] Failed to track API error:', posthogError);
        }
      }
      
      throw error;
    }
  };

  const currentPayments = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/current-payments`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  const addressVerified = async (): Promise<any | null> => {
    try {
      // Add cache-busting query parameter and better error handling
      const response: any = await $fetch(
        `/api/integration/address-verified?t=${Date.now()}`,
        {
          method: "GET",
          credentials: "include",
          // Add timeout and retry options
          retry: 2,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Return empty array instead of throwing to prevent cascading errors
      console.error(
        "Error fetching address data (no sensitive details logged)"
      );
      return { body: [] };
    }
  };
  const saveAddress = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/save-address`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const recharges = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/recharges`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const deleteAddress = async (addressId: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/delete-address`, {
        method: "POST",
        credentials: "include",
        body: { addressId },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const subscriptions = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/subscriptions`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const updateSubscriptions = async (payload: object): Promise<any | null> => {
    try {
      // Security: Error handling without logging for security (2025-05-05));
      const response: any = await $fetch(`/api/integration/comms-preferences`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const deletePayment = async (paymentId: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/delete-payment`, {
        method: "POST",
        credentials: "include",
        body: { paymentId },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const savePayment = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/save-payment`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const updatePayment = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/update-payment`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  const orders = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/orders`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const activatePending = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/activate-pending`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const rechargeAddon = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/recharge-addon`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  const recharge = async (payload: object): Promise<any | null> => {
    try {
      // Remove hardcoded goPrepaid segment to prevent duplication (n8n adds it automatically)
      // This follows the GO Commerce Integration API pattern documented in go-integration-api-guide.md
      console.log(
        "[useMobilePlan.service] Making recharge request with payload:",
        payload
      );
      const response: any = await $fetch(`/api/integrate/recharge`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  const rechargePlanChange = async (payload: object): Promise<any | null> => {
    try {
      // Use the exact path format the backend expects (camelCase)
      // This will be properly proxied through to GO Integrate
      // Security: Error handling without logging for security (2025-05-05)

      const response: any = await $fetch(`/api/integrate/recharge/planChange`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      // Security: Error handling without logging for security (2025-05-05)
      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  const verifyMsn = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/verify-msn`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  const msnPortNumber = async (portNumber: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/msn-port-number`, {
        method: "POST",
        credentials: "include",
        body: { portNumber },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  const dvsSources = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/dvs-sources`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const tariff = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/tariff`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };

  /**
   * Enable or disable auto-recharge for a customer service
   * Using the fixed auto-recharge endpoint that correctly uses PATCH to backend
   */
  const autoRecharge = async (payload: object): Promise<any | null> => {
    try {
      console.log(
        "Mobile Plan Service: Calling auto-recharge with payload:",
        payload
      );
      const response: any = await $fetch(
        `/api/integration/auto-recharge`, // Use endpoint that properly forwards with PATCH method to backend
        {
          method: "POST",
          body: payload,
          credentials: "include",
        }
      );

      return response?.result || null;
    } catch (error) {
      console.error("Auto-recharge error:", error);
      throw error;
    }
  };

  const validatePayment = async (paymentId: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/validate-payment`, {
        method: "POST",
        credentials: "include",
        body: { paymentId },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const checkoutPayment = async (paymentId: string): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/checkout-payment`, {
        method: "POST",
        credentials: "include",
        body: { paymentId },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const serviceAddressListingPreference = async (
    payload: object
  ): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/service-address-listing-preference`,
        {
          method: "POST",
          credentials: "include",
          body: { ...payload },
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  /**
   * Get service port details for a customer service
   * Uses secure server proxy endpoint instead of direct backend URL
   * Requires serviceId in request body for POST request
   */
  const servicePortDetails = async (serviceId: string): Promise<any | null> => {
    try {
      // IMPORTANT: Pass serviceId in request BODY for POST request
      const response: any = await $fetch(
        `/api/integration/service-port-details`,
        {
          method: "POST",
          credentials: "include",
          body: { serviceId: serviceId },
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const verificationPayment = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/verification-payment`,
        {
          method: "POST",
          body: payload,
          credentials: "include",
        }
      );

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const isNewCustomer = async (): Promise<any | null> => {
    try {
      const response: any = await $fetch(`/api/integration/is-new-customer`, {
        method: "GET",
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  return {
    networkBalance,
    currentServices,
    currentPayments,
    addressVerified,
    saveAddress,
    recharges,
    deleteAddress,
    subscriptions,
    updateSubscriptions,
    deletePayment,
    savePayment,
    updatePayment,
    orders,
    activatePending,
    rechargeAddon,
    recharge,
    rechargePlanChange,
    verifyMsn,
    msnPortNumber,
    dvsSources,
    tariff,
    autoRecharge,
    validatePayment,
    checkoutPayment,
    serviceAddressListingPreference,
    servicePortDetails,
    verificationPayment,
    isNewCustomer,
  };
};
